import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { allowedTypesForRole, willNeedApproval, APPROVER_FOR_TYPE, ROLE_LABEL, effectiveRoles, getCategory } from "../lib/permissions";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Info, ShieldCheck } from "lucide-react";

export default function CreateEventDialog({ open, onOpenChange, user, onCreated, users = [], event = null }) {
  const myRoles = effectiveRoles(user);
  const types = allowedTypesForRole(myRoles);
  const isEdit = !!event;
  const [form, setForm] = useState({
    event_type: types[0] || "",
    title: "",
    date: "",
    address: "",
    start_time: "",
    end_time: "",
    required_forms: false,
    form_proof: "",
    form_description: "",
    form_attachment: "",
    amount: "",
    remind: true,
    audience: "everyone",
    targets: [],
    mandatory: myRoles.includes("marshal"),
    all_day: false,
    end_date: "",
    recurrence: "none",
    recurrence_end: "",
    description: "",
    duration: "",
    countsHours: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [hoursMap, setHoursMap] = useState({});
  const candidateUsers = users.filter(u => u.role === "candidate" || (u.roles || []).includes("candidate"));

  useEffect(() => {
    if (open && form.event_type === "Candidate Event") {
      api.get("/candidates/hours", { params: form.date ? { week: form.date } : {} })
        .then(r => setHoursMap(Object.fromEntries(r.data.candidates.map(c => [c.id, c.used_hours]))))
        .catch(() => {});
    }
  }, [open, form.event_type, form.date]);

  useEffect(() => {
    if (!open) return;
    if (event) {
      setForm({
        event_type: event.event_type,
        title: event.title === event.event_type ? "" : (event.title || ""),
        date: event.date || "",
        end_date: event.end_date || "",
        address: event.address || "",
        start_time: event.all_day ? "" : (event.start_time || ""),
        end_time: event.all_day ? "" : (event.end_time || ""),
        required_forms: event.required_forms || false,
        form_proof: event.form_proof || "",
        form_description: event.form_description || "",
        form_attachment: event.form_attachment || "",
        amount: event.amount != null ? String(event.amount) : "",
        remind: event.reminders_enabled ?? true,
        audience: (event.target_user_ids || []).length ? "specific" : "everyone",
        targets: event.target_user_ids || [],
        mandatory: event.mandatory_attendance || false,
        all_day: event.all_day || false,
        recurrence: event.recurrence || "none",
        recurrence_end: event.recurrence_end || "",
        description: event.description || "",
        duration: event.duration_hours != null ? String(event.duration_hours) : "",
        countsHours: event.counts_toward_hours ?? true,
      });
    } else {
      setForm({
        event_type: types[0] || "", title: "", date: "", end_date: "", address: "",
        start_time: "", end_time: "", required_forms: false, form_proof: "",
        form_description: "", form_attachment: "", amount: "", remind: true,
        audience: "everyone", targets: [], mandatory: myRoles.includes("marshal"),
        all_day: false, recurrence: "none", recurrence_end: "",
        description: "", duration: "", countsHours: true,
      });
    }
  }, [open, event]);

  const needs = willNeedApproval(myRoles, form.event_type);
  const approver = APPROVER_FOR_TYPE[form.event_type];
  const isPayment = form.event_type === "Payment Plan";
  const isCandidate = form.event_type === "Candidate Event";
  const prompts = getCategory(form.event_type)?.prompts || { forms: true, location: true, times: true };

  const submit = async () => {
    if (!form.event_type || !form.date || (!form.all_day && (!form.start_time || !form.end_time))) {
      toast.error("Fill all required fields"); return;
    }
    if (form.recurrence === "weekly" && !form.recurrence_end) {
      toast.error("Pick a repeat-until date for weekly events"); return;
    }
    if (!isPayment && prompts.location !== false && !form.address.trim()) {
      toast.error("Address is required"); return;
    }
    if (form.required_forms && !form.form_description.trim() && !form.form_proof.trim()) {
      toast.error("Describe the required form"); return;
    }
    if (isPayment && (!form.amount || parseFloat(form.amount) <= 0)) {
      toast.error("Enter a payment amount"); return;
    }
    if (isPayment && form.audience === "specific" && form.targets.length === 0) {
      toast.error("Select at least one member"); return;
    }
    if (isCandidate) {
      if (!form.description.trim()) { toast.error("Describe what the candidate will do"); return; }
      if (form.targets.length === 0) { toast.error("Select at least one candidate"); return; }
      if (!form.duration || parseFloat(form.duration) <= 0) { toast.error("Enter the hours needed"); return; }
    }
    setSubmitting(true);
    try {
      const payload = {
        event_type: form.event_type, title: form.title, date: form.date,
        address: form.address, start_time: form.start_time, end_time: form.end_time,
        required_forms: form.required_forms, form_proof: form.form_proof,
        form_description: form.form_description, form_attachment: form.form_attachment,
        all_day: form.all_day || prompts.times === false,
      };
      if (form.end_date && form.end_date > form.date) payload.end_date = form.end_date;
      if (form.recurrence === "weekly") {
        payload.recurrence = "weekly";
        payload.recurrence_end = form.recurrence_end;
      }
      if (isPayment) {
        payload.amount = parseFloat(form.amount);
        payload.reminders_enabled = form.remind;
      }
      if (isPayment) {
        payload.target_user_ids = form.audience === "specific" ? form.targets : [];
      }
      if (isCandidate) {
        payload.target_user_ids = form.targets;
        payload.mandatory_attendance = form.mandatory;
        payload.description = form.description;
        payload.duration_hours = parseFloat(form.duration);
        payload.counts_toward_hours = form.countsHours;
      }
      if (isEdit) {
        await api.patch(`/events/${event.id}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/events", payload);
        toast.success(needs ? "Submitted for chair approval" : "Event added to calendar");
      }
      onOpenChange(false);
      setForm({ ...form, title: "", date: "", address: "", start_time: "", end_time: "", form_proof: "", form_description: "", form_attachment: "", required_forms: false, amount: "", remind: true, audience: "everyone", targets: [], mandatory: false, all_day: false, end_date: "", recurrence: "none", recurrence_end: "", description: "", duration: "" });
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create event");
    } finally { setSubmitting(false); }
  };

  if (types.length === 0) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-slate-950 border-slate-800">
          <DialogHeader>
            <DialogTitle>Not authorized</DialogTitle>
            <DialogDescription>Members cannot create events. Ask your President to change your rank.</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-[#866D4B]/40 w-[calc(100vw-1.5rem)] sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="create-event-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[var(--sn-gold)]">{isEdit ? "Edit Event" : "New Event"}</DialogTitle>
          <DialogDescription className="text-slate-400">
            Signed in as <span className="text-slate-200">{ROLE_LABEL[user.role]}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Event Type *</Label>
            <Select value={form.event_type} disabled={isEdit} onValueChange={(v) => setForm({ ...form, event_type: v, mandatory: v === "Candidate Event" ? myRoles.includes("marshal") : false })}>
              <SelectTrigger data-testid="event-type-select" className="bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 max-h-72">
                {types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {form.event_type && (
            <div className={`rounded-lg p-3 text-sm flex items-start gap-2 border ${
              needs ? "bg-amber-950/40 border-amber-700 text-amber-200"
                    : "bg-emerald-950/40 border-emerald-800 text-emerald-200"
            }`}>
              {needs ? <Info className="w-4 h-4 mt-0.5" /> : <ShieldCheck className="w-4 h-4 mt-0.5" />}
              <div>
                {needs
                  ? <>This event will be marked <b>pending</b> and requires approval from the <b>{ROLE_LABEL[approver]}</b>.</>
                  : <>You have <b>instant publish rights</b> for this event type.</>}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Title (optional)</Label>
            <Input data-testid="event-title-input" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Founders Day Formal" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>{isPayment ? "Due Date *" : "Start Date *"}</Label>
              <Input data-testid="event-date-input" type="date" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>End Date <span className="text-xs text-slate-500">(multi-day)</span></Label>
              <Input data-testid="event-end-date-input" type="date" value={form.end_date} min={form.date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
          </div>

          {prompts.times !== false && (
          <>
          <div className="flex items-center justify-between rounded-lg border border-slate-700 p-3 bg-slate-900/50">
            <div>
              <Label className="text-slate-200">All-day event</Label>
              <p className="text-xs text-slate-500">No specific start/end times</p>
            </div>
            <Switch data-testid="all-day-toggle" checked={form.all_day}
              onCheckedChange={(v) => setForm({ ...form, all_day: v })} />
          </div>

          {!form.all_day && (
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label className="text-xs">Start *</Label>
                <Input data-testid="event-start-time-input" type="time" value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">End *</Label>
                <Input data-testid="event-end-time-input" type="time" value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
              </div>
            </div>
          )}
          </>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Repeats</Label>
              <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                <SelectTrigger data-testid="recurrence-select" className="bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="none">Does not repeat</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.recurrence === "weekly" && (
              <div className="space-y-2">
                <Label>Repeat until *</Label>
                <Input data-testid="recurrence-end-input" type="date" min={form.date}
                  value={form.recurrence_end}
                  onChange={(e) => setForm({ ...form, recurrence_end: e.target.value })} />
              </div>
            )}
          </div>

          {prompts.location !== false && (
          <div className="space-y-2">
            <Label>Address {isPayment ? "" : "*"}</Label>
            <Input data-testid="event-address-input" value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder={isPayment ? "Optional (e.g. Chapter House)" : "123 Greek Row, University Ave"} />
          </div>
          )}

          {isPayment && (
            <div className="space-y-3 rounded-lg border border-lime-800/60 bg-lime-950/20 p-3" data-testid="payment-plan-section">
              <div className="space-y-2">
                <Label>Amount Due ($) *</Label>
                <Input data-testid="payment-amount-input" type="number" min="0" step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="250.00" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-slate-200">Payment Reminders</Label>
                  <p className="text-xs text-slate-500">Notify at 2 weeks, 1 week, 3 days, 1 day before due date</p>
                </div>
                <Switch data-testid="payment-reminders-toggle" checked={form.remind}
                  onCheckedChange={(v) => setForm({ ...form, remind: v })} />
              </div>
              <div className="space-y-2">
                <Label>Applies To</Label>
                <div className="flex gap-2">
                  <button type="button" data-testid="audience-everyone"
                    onClick={() => setForm({ ...form, audience: "everyone", targets: [] })}
                    className={`text-xs px-3 py-1.5 rounded-full border ${form.audience === "everyone" ? "bg-lime-500 text-slate-950 border-lime-400 font-semibold" : "bg-slate-900 text-slate-400 border-slate-700"}`}>
                    Entire Chapter
                  </button>
                  <button type="button" data-testid="audience-specific"
                    onClick={() => setForm({ ...form, audience: "specific" })}
                    className={`text-xs px-3 py-1.5 rounded-full border ${form.audience === "specific" ? "bg-lime-500 text-slate-950 border-lime-400 font-semibold" : "bg-slate-900 text-slate-400 border-slate-700"}`}>
                    Specific Members
                  </button>
                </div>
                {form.audience === "specific" && (
                  <div className="max-h-40 overflow-y-auto rounded-md border border-slate-700 bg-slate-900/60 p-2 space-y-0.5" data-testid="payment-target-list">
                    {users.map(u => (
                      <label key={u.id} data-testid={`payment-target-${u.id}`}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer text-sm text-slate-300">
                        <input type="checkbox"
                          checked={form.targets.includes(u.id)}
                          onChange={(e) => setForm({
                            ...form,
                            targets: e.target.checked ? [...form.targets, u.id] : form.targets.filter(x => x !== u.id),
                          })}
                          className="accent-lime-500" />
                        {u.name} <span className="text-xs text-slate-500">({u.email})</span>
                      </label>
                    ))}
                  </div>
                )}
                {form.audience === "specific" && (
                  <p className="text-[11px] text-slate-500">Only selected members (plus you, President & Admin) will see this payment plan.</p>
                )}
              </div>
            </div>
          )}

          {isCandidate && (
            <div className="space-y-3 rounded-lg border border-sky-800/60 bg-sky-950/20 p-3" data-testid="candidate-event-section">
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea data-testid="candidate-description-input" rows={3} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What will the candidate be doing? (visible to candidate, requester & marshal)" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Hours needed *</Label>
                  <Input data-testid="candidate-hours-input" type="number" min="0.5" max="20" step="0.5"
                    value={form.duration}
                    onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="2" />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-slate-700 p-3 bg-slate-900/50">
                  <div>
                    <Label className="text-slate-200 text-xs">Mandatory</Label>
                    <p className="text-[10px] text-slate-500">Required attendance</p>
                  </div>
                  <Switch data-testid="mandatory-toggle" checked={form.mandatory}
                    onCheckedChange={(v) => setForm({ ...form, mandatory: v })} />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-slate-700 p-3 bg-slate-900/50 col-span-2">
                  <div>
                    <Label className="text-slate-200 text-xs">Counts toward 20h/week</Label>
                    <p className="text-[10px] text-slate-500">Off = doesn&apos;t use the candidate&apos;s availability</p>
                  </div>
                  <Switch data-testid="counts-hours-toggle" checked={form.countsHours}
                    onCheckedChange={(v) => setForm({ ...form, countsHours: v })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Candidate(s) * <span className="text-[10px] text-slate-500">— 20h weekly availability</span></Label>
                <div className="max-h-44 overflow-y-auto rounded-md border border-slate-700 bg-slate-900/60 p-2 space-y-0.5" data-testid="candidate-target-list">
                  {candidateUsers.map(u => {
                    const used = hoursMap[u.id] ?? 0;
                    const over = used + (parseFloat(form.duration) || 0) > 20;
                    return (
                      <label key={u.id} data-testid={`candidate-target-${u.id}`}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer text-sm text-slate-300">
                        <input type="checkbox"
                          checked={form.targets.includes(u.id)}
                          onChange={(e) => setForm({
                            ...form,
                            targets: e.target.checked ? [...form.targets, u.id] : form.targets.filter(x => x !== u.id),
                          })}
                          className="accent-sky-500" />
                        <span className="flex-1">{u.name}</span>
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${over ? "bg-rose-950 text-rose-300 border-rose-800" : "bg-sky-950 text-sky-300 border-sky-800"}`}>
                          {used}/20h
                        </span>
                      </label>
                    );
                  })}
                  {!candidateUsers.length && <div className="text-xs text-slate-500 p-2">No candidates on the roster yet.</div>}
                </div>
                <p className="text-[11px] text-slate-500">Only the selected candidate(s), you, and the Marshal can see this event. The Marshal approves it before it posts.</p>
              </div>
            </div>
          )}

          {prompts.forms !== false && (
          <div className="flex items-center justify-between rounded-lg border border-slate-700 p-3 bg-slate-900/50">
            <div>
              <Label className="text-slate-200">Required Forms</Label>
              <p className="text-xs text-slate-500">e.g. liability waivers, risk management forms</p>
            </div>
            <Switch data-testid="required-forms-toggle" checked={form.required_forms}
              onCheckedChange={(v) => setForm({ ...form, required_forms: v })} />
          </div>
          )}

          {prompts.forms !== false && form.required_forms && (
            <div className="space-y-3 rounded-lg border border-emerald-800/50 bg-emerald-950/20 p-3" data-testid="forms-section">
              <div className="space-y-2">
                <Label>Form description *</Label>
                <Textarea data-testid="form-description-input" rows={2}
                  value={form.form_description}
                  onChange={(e) => setForm({ ...form, form_description: e.target.value })}
                  placeholder="e.g. University event registration + liability waiver, due to VP by Friday" />
              </div>
              <div className="space-y-2">
                <Label>Attach the form (optional)</Label>
                {form.form_attachment ? (
                  <div className="flex items-center justify-between rounded-md border border-emerald-700 bg-black/30 px-3 py-2">
                    <span className="text-xs text-emerald-300">Form attached ✓</span>
                    <button type="button" data-testid="form-attachment-remove"
                      onClick={() => setForm({ ...form, form_attachment: "" })}
                      className="text-xs text-rose-300 hover:text-rose-200">Remove</button>
                  </div>
                ) : (
                  <label data-testid="form-attachment-input"
                    className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-emerald-700 bg-black/30 px-3 py-3 text-sm text-emerald-200 cursor-pointer hover:border-emerald-500">
                    Attach file (image or PDF, under 1.5MB)
                    <input type="file" accept="image/*,application/pdf" className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (f.size > 1_500_000) { toast.error("File too large (keep under 1.5MB)"); return; }
                        const reader = new FileReader();
                        reader.onload = () => setForm((prev) => ({ ...prev, form_attachment: reader.result }));
                        reader.readAsDataURL(f);
                      }} />
                  </label>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-slate-400">Proof of submission (optional reference)</Label>
                <Input data-testid="form-proof-input" value={form.form_proof}
                  onChange={(e) => setForm({ ...form, form_proof: e.target.value })}
                  placeholder="Confirmation # or link (optional)" />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="submit-event-button" onClick={submit} disabled={submitting}
            className="bg-[var(--sn-gold)] hover:brightness-95 text-black font-bold">
            {submitting ? "Saving…" : (isEdit ? "Save Changes" : (needs ? "Submit for Approval" : "Publish Event"))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
